import {
  Alert,
  Box,
  Button,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useState } from 'react';
import useAuth from '../../../hooks/useAuth';
import { useTranslation } from '../../../hooks/useTranslation';
import { useSubscription } from '../../../contexes/SubscriptionProvider';
import { updateCompany } from '../../../services/companyService';
import LowBalanceBanner from '../LowBalanceBanner/LowBalanceBanner';
import NavBar from '../NavBar/NavBar';
import PrivateAppTopBar from '../NavBar/PrivateAppTopBar';
import Sidebar from '../Sidebar/Sidebar';

const SIDEBAR_COLLAPSED_STORAGE_KEY = 'sidebar_collapsed_desktop';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout = ({ children }: LayoutProps): JSX.Element => {
  const { currentUser } = useAuth();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    const savedValue = window.localStorage.getItem(
      SIDEBAR_COLLAPSED_STORAGE_KEY
    );
    return savedValue === '1';
  });
  const [isDismissingAgentWarning, setIsDismissingAgentWarning] =
    useState(false);
  const { company, refreshSubscription } = useSubscription();

  const handleSidebarToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleSidebarClose = () => {
    setSidebarOpen(false);
  };

  const sidebarWidth = isSidebarCollapsed ? 92 : 280;
  const forcedOfflineCount =
    company?.agent_limit_warning_forced_offline_count ?? 0;
  const warningMessage =
    company?.agent_limit_warning_message ??
    t(
      'Some agents were automatically set offline because your current plan allows up to {{count}} agents.',
      { count: company?.agent_limit_warning_max_agents ?? 0 }
    );

  const handleDismissAgentLimitWarning = async () => {
    if (!currentUser?.company_id || !company?.agent_limit_warning) return;
    setIsDismissingAgentWarning(true);
    try {
      await updateCompany(currentUser.company_id, {
        agent_limit_warning: false,
        agent_limit_warning_message: null,
      });
      await refreshSubscription();
    } catch (error) {
      console.error('Failed to dismiss agent limit warning banner', error);
    } finally {
      setIsDismissingAgentWarning(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#F6F8FC' }}>
      {/* Sidebar - only show for authenticated users on desktop */}
      {currentUser && !isMobile && (
        <Box
          sx={{
            width: sidebarWidth,
            flexShrink: 0,
            height: '100vh',
            position: 'sticky',
            top: 0,
            zIndex: theme.zIndex.drawer,
          }}
        >
          <Sidebar
            isOpen={true}
            onClose={handleSidebarClose}
            isMobile={false}
            isCollapsed={isSidebarCollapsed}
            onToggleCollapse={() => {
              setIsSidebarCollapsed(prevCollapsed => {
                const nextCollapsed = !prevCollapsed;
                window.localStorage.setItem(
                  SIDEBAR_COLLAPSED_STORAGE_KEY,
                  nextCollapsed ? '1' : '0'
                );
                return nextCollapsed;
              });
            }}
          />
        </Box>
      )}

      {/* Mobile sidebar - overlay */}
      {currentUser && isMobile && (
        <Sidebar
          isOpen={sidebarOpen}
          onClose={handleSidebarClose}
          isMobile={true}
        />
      )}

      {/* Main content area */}
      <Box
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          minWidth: 0, // Allow flex item to shrink below content size
          width: '100%',
        }}
      >
        {/* Top navigation bar - only show for unauthenticated users (public pages) */}
        {!currentUser && (
          <Box
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: theme.zIndex.appBar - 1,
              bgcolor: 'background.paper',
              px: '32px',
              py: '24px',
            }}
          >
            <NavBar />
          </Box>
        )}

        {/* Logged-in top strip (pricing v2): usage on desktop */}
        {currentUser && !isMobile && <PrivateAppTopBar />}

        {/* Downgrade warning: backend flagged that some agents were forced offline */}
        {currentUser && company?.agent_limit_warning && (
          <Box
            sx={{
              px: currentUser ? { xs: 1, md: 3 } : 0,
              pt: currentUser ? 2 : 0,
            }}
          >
            <Alert
              severity="warning"
              action={
                <Button
                  color="inherit"
                  size="small"
                  disabled={isDismissingAgentWarning}
                  onClick={handleDismissAgentLimitWarning}
                  startIcon={
                    isDismissingAgentWarning ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : null
                  }
                >
                  {t('Dismiss')}
                </Button>
              }
            >
              {warningMessage}
              {forcedOfflineCount > 0
                ? ` ${t('({{count}} affected)', { count: forcedOfflineCount })}`
                : ''}
            </Alert>
          </Box>
        )}

        {/* Low balance: prompt to upgrade before credits run out (self-gating) */}
        {currentUser && <LowBalanceBanner />}

        {/* Mobile sidebar toggle for authenticated users */}
        {currentUser && isMobile && (
          <Box
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: theme.zIndex.appBar - 1,
              bgcolor: '#F6F8FC',
              px: { xs: 1, md: 2 },
              py: 1.25,
            }}
          >
            <NavBar onSidebarToggle={handleSidebarToggle} />
          </Box>
        )}

        {/* Page content */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            // Unified gray app canvas for authenticated pages.
            bgcolor: currentUser ? '#F6F8FC' : 'transparent',
            // Only add padding for authenticated users (private pages with sidebar)
            p: currentUser ? { xs: 1, md: 2.5 } : 0,
            minHeight: 0, // Allow content to shrink
            // For public pages, make content fill remaining space
            display: !currentUser ? 'flex' : 'block',
            flexDirection: !currentUser ? 'column' : undefined,
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default Layout;
